import { z } from 'zod';

import { isoDateToDate } from './dates';
import { fixture } from './fixtures';
import { RuleGroup, ruleGroupFixture } from './groups';

// TypeScript enums
export type CelebrationState = 'active' | 'inactive';

export enum CelebrationType {
	Birthday = 'birthday',
	Custom = 'custom_yearly_date',
	NewHire = 'new_hire',
	Welcome = 'welcome',
	WorkAnniversary = 'work_anniversary',
	Wellness = 'wellness',
}

export enum CelebrationVisibility {
	Public = 'publicly_visible',
	Private = 'privately_visible',
	Team = 'immediate_team',
}

export enum CelebrationWorkAnniversaryPointSetting {
	EveryYearWithSameAmount = 'everyYearWithSameAmount',
	EveryYearWithCustomAmount = 'everyYearWithCustomAmount',
	OnlySpecificYears = 'onlySpecificYears',
}

// Base schemas
export const YearAmountMapping = z.object({
	yearThreshold: z.number(),
	amount: z.number(),
});

export type YearAmountMapping = z.infer<typeof YearAmountMapping>;

export const Celebration = z.object({
	id: z.string(),
	name: z.string(),
	type: z.enum(CelebrationType),
	giverId: z.string(),
	headline: z.string(),
	amount: z.number().nullable(),
	includeBenefactors: z.boolean(),
	daysToDelaySend: z.number().nullable(),
	reason: z.string().nullable(),
	imageUrl: z.string().nullable(),
	iconUrl: z.string().nullable(),
	state: z.enum(['active', 'inactive']),
	visibility: z.enum(CelebrationVisibility),
	yearAmountMappings: z.array(YearAmountMapping),
	receivingRulesGroup: RuleGroup.nullable(),
	celebrationDate: isoDateToDate.nullable(),
	shiftWeekendCelebrations: z.boolean(),
});

export type Celebration = z.infer<typeof Celebration>;

export const CelebrationDetail = Celebration.extend({
	// CelebrationDetail has the same fields as Celebration in this case
});

export type CelebrationDetail = z.infer<typeof CelebrationDetail>;

// Fixture functions
export const yearAmountMappingFixture = (overrides: Partial<YearAmountMapping> = {}): YearAmountMapping => ({
	yearThreshold: fixture.int(1, 10),
	amount: fixture.int(100, 5000),
	...overrides,
});

export const celebrationFixture = (overrides: Partial<Celebration> = {}): Celebration => ({
	id: fixture.id(),
	name: fixture.name('Celebration'),
	type: CelebrationType.Welcome,
	giverId: fixture.id(),
	headline: fixture.name('Celebration Headline'),
	amount: fixture.int(50, 1000),
	includeBenefactors: fixture.boolean(),
	daysToDelaySend: null,
	reason: '@recipient here is +250 for your celebration',
	imageUrl: fixture.url('celebration'),
	iconUrl: fixture.url('icon'),
	state: 'active',
	visibility: CelebrationVisibility.Public,
	yearAmountMappings: [],
	receivingRulesGroup: ruleGroupFixture(),
	celebrationDate: fixture.date(),
	shiftWeekendCelebrations: true,
	...overrides,
});

export const celebrationDetailFixture = (overrides: Partial<CelebrationDetail> = {}): CelebrationDetail => ({
	...celebrationFixture(),
	...overrides,
});

// Utility functions
export const getCelebrationEditPath = (id: string): string => `/admin/programs/celebrations/${id}/edit`;

export const isCelebrationGlobal = (celebration: { receivingRulesGroup: RuleGroup | null }): boolean =>
	!celebration.receivingRulesGroup || celebration.receivingRulesGroup.conditions.length === 0;

export const isCelebrationWorkAnniversary = (celebration: { type: CelebrationType }): boolean => celebration.type === CelebrationType.WorkAnniversary;

export const isCelebrationWellness = (celebration: { type: CelebrationType }): boolean => celebration.type === CelebrationType.Wellness;

export const isCelebrationCustom = (celebration: { type: CelebrationType }): boolean => celebration.type === CelebrationType.Custom;

export const isCelebrationInactive = (celebration: { state: CelebrationState }): boolean => celebration.state === 'inactive';
